import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Plus, Edit3, X, Boxes } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Models() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", capacity: "", notes: "" });

  const load = useCallback(() => api.get("/models").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm({ name: "", capacity: "", notes: "" }); setShowForm(true); };
  const startEdit = (m) => { setEditing(m); setForm({ name: m.name, capacity: m.capacity || "", notes: m.notes || "" }); setShowForm(true); };

  const onSave = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing) await api.put(`/models/${editing.id}`, form);
      else await api.post("/models", form);
      toast.success("Saved"); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    try { await api.delete(`/models/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout title="Product Models" subtitle="Manage product catalog used across DPR"
      actions={<button data-testid="model-add-btn" onClick={startNew} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16} /> New Model</button>}
    >
      <Toaster richColors position="top-right" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full section-card text-center text-slate-400 py-10">No models yet.</div>}
        {items.map((m) => (
          <div key={m.id} className="section-card hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center"><Boxes size={22} /></div>
              <div className="flex gap-1">
                <button data-testid={`model-edit-${m.id}`} onClick={() => startEdit(m)} className="text-slate-400 hover:text-blue-600 p-1"><Edit3 size={16} /></button>
                <ConfirmDelete testId={`model-delete-${m.id}`} onConfirm={() => onDelete(m.id)} title="Delete this model?" />
              </div>
            </div>
            <div className="mt-3">
              <div className="font-heading font-semibold text-slate-900">{m.name}</div>
              {m.capacity && <div className="text-sm text-slate-500 mt-1">Capacity: {m.capacity}</div>}
              {m.notes && <div className="text-xs text-slate-400 mt-2 line-clamp-2">{m.notes}</div>}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="font-heading text-xl font-semibold">{editing ? "Edit Model" : "New Model"}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label><input data-testid="model-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. General Yodha 1000ML" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Capacity</label><input data-testid="model-capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="e.g. 1000ML" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Notes</label><textarea data-testid="model-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="3" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white">Cancel</button>
              <button data-testid="model-save-btn" onClick={onSave} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
